import React, { Fragment, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TitleBar, GroupTitle } from 'styles/layout/titlebar';
import DatabaseService from 'services/database';
import { setGlobalErrorMsg, setGlobalPleaseWaitMsg, setGlobalSuccessMsg } from 'store/global/globalActions';
import useTitle from 'hooks/useTitle';
import { Content } from 'styles/globalStyles';
import WPSDataTable from 'components/wpstaq/WPSDataTable/WPSDataTable';
import { websitesSelector } from 'store/website/websiteSelectors';
import useConfirm from 'hooks/useConfirm';
import JsxHelper from 'helpers/jsx';
import TableHelper from 'helpers/table';
import DialogHelper from 'helpers/dialog';
import WebsiteHelper from 'helpers/website';
import { isEmptyOrNull, sortByNumericField } from 'helpers';

const ServerIndex = () => {
  useTitle('Databases');
  const dispatch = useDispatch();
  const allWebsites = useSelector(websitesSelector);
  const [loading, setLoading] = useState(false);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const [databases, setDatabases] = useState([]);
  const [modal, setModal] = useState(false);
  const [websitesData, setWebsitesData] = useState([]);
  const [currentScreen, setCurrentScreen] = useState('databases');
  const [diagnostics, setDiagnostics] = useState(false);
  const [currentDatabase, setCurrentDatabase] = useState(false);
  const [activeDiagnosticsTab, setActiveDiagnosticsTab] = useState('processlist');
  const confirm = useConfirm();
  const mounted = useRef(true);

  useEffect(() => {
    loadDatabases();
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line
  }, []);

  // ----------
  // HELPERS
  // ----------

  const initDatabaseObject = (database) => {
    const totalSites = database.website_slugs ? database.website_slugs.length : 0;
    return { ...database, total_sites: totalSites };
  }

  // ----------
  // API
  // ----------

  const loadDatabases = () => {
    setLoading(true);
    DatabaseService.list({})
      .then(databases => {
        if (mounted.current) {
          databases = databases.map(initDatabaseObject);
          setDatabases(sortByNumericField(databases, 'total_sites', false));
        }
      })
      .catch(error => dispatch(setGlobalErrorMsg(error)))
      .finally(() => setLoading(false));
  }

  const deleteDatabase = (item) => {
    dispatch(setGlobalPleaseWaitMsg({ model: 'database', action: 'delete', id: item.slug }));
    DatabaseService.destroy({ database_slug: item.slug })
      .then(() => {
        if (mounted.current) {
          setDatabases(databases.filter(d => d.slug !== item.slug));
          dispatch(setGlobalSuccessMsg({ model: 'database', id: item.slug, action: 'deleted' }));
        }
      })
      .catch(error => dispatch(setGlobalErrorMsg(error)));
  }

  const refreshDatabase = (item) => {
    dispatch(setGlobalPleaseWaitMsg({ model: 'database', action: 'refresh', id: item.slug }));
    DatabaseService.refresh({ database_slug: item.slug })
      .then((database) => {
        if (mounted.current) {
          const index = databases.findIndex(d => d.slug === database.slug);
          if (index !== -1) {
            const newDatabases = [...databases];
            newDatabases[index] = initDatabaseObject(database);
            setDatabases(sortByNumericField(newDatabases, 'total_sites', false));
            dispatch(setGlobalSuccessMsg({ model: 'database', id: database.slug, action: 'refreshed' }));
          } else {
            window.logHelper.error('Database not found in list');
          }
        }
      })
      .catch(error => dispatch(setGlobalErrorMsg(error)));
  }

  const attachWebsite = (database, websiteSlug) => {
    setModal(prevState => ({ ...prevState, loading: true }));
    dispatch(setGlobalPleaseWaitMsg({ model: 'database', action: 'attach', id: database.slug }));
    const data = { database_slug: database.slug, website_slug: websiteSlug };
    DatabaseService.attachWebsite(data)
      .then(() => dispatch(setGlobalPleaseWaitMsg({ model: 'database', id: database.slug, action: 'attached' })))
      .catch(error => dispatch(setGlobalErrorMsg(error)))
      .finally(() => setModal(false));
  }

  const detachWebsite = (database) => {
    dispatch(setGlobalPleaseWaitMsg({ model: 'database', action: 'detach', id: database.slug }));
    DatabaseService.detachWebsite({ database_slug: database.slug, website_slug: database.website_slug })
      .then(() => {
        dispatch(setGlobalPleaseWaitMsg({ model: 'database', id: database.slug, action: 'detached' }));
      })
      .catch(error => dispatch(setGlobalErrorMsg(error)));
  }

  const getDiagnostics = async (database, quick) => {
    const include = quick ? ['innodb', 'metrics'] : null;
    window.logHelper.info(`Loading diagnostics for database: ${database.slug}`);
    setLoadingDiagnostics(prev => ({ ...prev, [database.slug]: true }));
    return DatabaseService.getDiagnostics({ database_slug: database.slug, include })
      .then((data) => {
        setDiagnostics(prev => ({ ...prev, [database.slug]: data }));
        return data;
      })
      .catch(error => {
        dispatch(setGlobalErrorMsg(`Loading diagnostics for ${database.slug} failed.`));
        throw error;
      })
      .finally(() => {
        setLoadingDiagnostics(prev => ({ ...prev, [database.slug]: false }));
      });
  }

  // ---------
  // HANDLERS
  // ---------

  const onClickAttachWebsite = (database) => setModal({ database, website_slug: '', loading: false })

  const showWebsites = (database) => {
    const websites = database.website_slugs.map(website => {
      const websiteData = allWebsites.find(w => w.slug === website);
      if (!websiteData) {
        return null; // Not loaded yet
      }
      return { ...websiteData, database_slug: database.slug, website_slug: website };
    }).filter(Boolean);
    setWebsitesData(websites);
    setCurrentDatabase(database);
    setCurrentScreen('websites');
  }

  const showDiagnostics = (database) => {
    setCurrentScreen('diagnostics');
    setCurrentDatabase(database);
    getDiagnostics(database);
  }

  const loadDiagnosticsSync = async () => {
    for (const database of databases) {
      if (!diagnostics[database.slug]) {
        try {
          await getDiagnostics(database, true);
        } catch (error) {
          window.logHelper.error(`Error loading diagnostics for ${database.slug}:`, error);
        }
      }
    }
  };

  // ----------
  // MAIN TABLE
  // ----------

  const renderDatabaseAlerts = () => {
    // Get all alerts from diagnostics
    const alerts = {};
    for (const slug in diagnostics) {
      const data = diagnostics[slug];
      if (!isEmptyOrNull(data.alerts)) {
        alerts[slug] = data.alerts;
      }
    }
    if (isEmptyOrNull(alerts)) {
      return null; // No alerts
    }
    // Render alerts
    const alertStyle = { padding: '5px' }
    const containerStyle = { width: '500px', fontSize: '11.5px', padding: '5px 10px' };
    return <div className='result-box' style={containerStyle}>{Object.keys(alerts).map(slug => (
      <div key={slug}>
        {alerts[slug].map((alert) => {
          const content = <span><strong>{slug}:</strong> {alert.message}</span>;
          if (alert.type === 'warning') {
            return JsxHelper.createWarningBox(content, true, null, alertStyle);
          } else if (alert.type === 'critical') {
            return JsxHelper.createErrorBox(content, alertStyle);
          }
          return null;
        })}
      </div>
    ))}</div>;
  }

  const renderDiagnosticsState = (row) => {
    if (loadingDiagnostics && loadingDiagnostics[row.slug]) {
      return 'Loading...';
    }
    if (!diagnostics[row.slug]) {
      return 'No data loaded';
    }
    return false;
  }

  const actions = [
    {
      value: 'Websites',
      onClick: showWebsites,
    },
    {
      value: 'Refresh Details',
      onClick: refreshDatabase,
    },
    {
      value: 'View Diagnostics',
      onClick: showDiagnostics,
    },
    {
      value: 'Quick Check',
      onClick: (item) => getDiagnostics(item, true),
    },
    {
      value: 'Attach Website',
      doHide: row => !!row.website_slug,
      onClick: onClickAttachWebsite,
    },
    {
      value: 'Detach Website',
      doHide: row => !row.website_slug,
      onClick:  item => DialogHelper
        .confirmAction(confirm, 'detach', item.slug, 'database')
        .then(() => detachWebsite(item)),
    },
    {
      value: 'Delete',
      onClick:  item => DialogHelper
        .confirmDelete(confirm, item.slug, 'database')
        .then(() => deleteDatabase(item)),
    },
  ];

  const headers = [
    JsxHelper.createTableRegionHeader('9%'),
    JsxHelper.createTableTextHeaderWithCallback('database_slug', 'Database', '22%', row => {
      row = TableHelper.customizeCellValue(row, 'database_slug', row.partner_slug + '-' + row.slug); // Allows searching
      return <div style={{margin:'6px 0'}}>
        <span style={{ display: 'block', marginBottom: '7px', }}><strong>{row.partner_slug + '-' + row.slug}</strong></span>
        <span style={{ display: 'block', marginBottom: '7px', fontSize: '13px' }}>{row.instance_type}</span>
        {JsxHelper.createBubble({
          style: { marginLeft: '0', marginBottom: '5px' },
          background: row.is_shared ? 'success' : 'info',
          text: row.is_shared ? 'Shared' : 'Private',
          tooltip: row.is_shared ? 'This database can shared by multiple websites' : 'This database is private to one website',
          small: true,
        })}
        {row.is_staging ? JsxHelper.createBubble({
          style: { marginLeft: '0', marginBottom: '5px' },
          background: 'alternative',
          text: 'Staging',
          tooltip: 'This database is used for staging websites.',
          small: true,
        }) : null}
        {row.is_reserved && row.is_shared ? JsxHelper.createBubble({
          style: { marginLeft: '0', marginBottom: '5px' },
          background: 'primary',
          text: 'Reserved',
          tooltip: 'This database is not part of the shared pool. To attach a website to this database, admins must do it manually.',
          small: true,
        }) : null}
      </div>
    }),
    JsxHelper.createTableTextHeaderWithCallback('total_sites', 'Websites', '10%', row => row.total_sites, null, { paddingLeft: '10px !important' }),
    JsxHelper.createTableHiddenHeader('website_slugs', 'Websites', row => {
      row = TableHelper.customizeCellValue(row, 'website_slugs', row.website_slugs.join(', ')); // Allows searching
      return row.website_slugs;
    }),
    JsxHelper.createTableTextHeaderWithCallback('storage', 'Storage', '10%', row => {
      return JsxHelper.createTableMultiLineCell({
        header: `${row.storage} GB`,
        subheader: row.max_storage === '-' ? 'No Autoscale' : `Max: ${row.max_storage} GB`,
      })
    }),
    JsxHelper.createTableTextHeaderWithCallback('status', 'Innodb Buffer Pool', '23%', row => {
      const state = renderDiagnosticsState(row);
      if (state) {
        return state; // Loading or no data available
      }
      const data = diagnostics[row.slug].innodb_parsed.buffer_pool;
      if (!data) {
        return 'No data available';
      }
      const innodb = {
        'Memory Allocated': (data.total_memory ? (data.total_memory.value / 1024 / 1024 / 1024).toFixed(2) + ' GB' : 'N/A'),
        'Dictionary Memory': `${data.dictionary_memory ? (data.dictionary_memory.value / 1024 / 1024).toFixed(2) + ' MB' : 'N/A'}`,
        'Buffer Pool Hit Rate': `${data.hit_rate ? data.hit_rate.value + '%' : 'N/A'}`,
        'Buffer Pool Size': `${data.buffer_pool_size ? data.buffer_pool_size.value : 'N/A'}`,
        'Free Buffers': `${data.free_buffers ? data.free_buffers.value : 'N/A'}`,
        'Database Pages': `${data.database_pages ? data.database_pages.value : 'N/A'}`,
        'Dirty Pages': `${data.dirty_page_percent ? data.dirty_page_percent.value + '%' : 'N/A'}`,
        'Pending Reads': `${data.pending_reads ? data.pending_reads.value : 'N/A'}`,
      };
      return JsxHelper.createKeyValueFromObject(innodb)
    }),
    JsxHelper.createTableTextHeaderWithCallback('status', 'CloudWatch Metrics', '23%', row => {
      const state = renderDiagnosticsState(row);
      if (state) {
        return state; // Loading or no data available
      }
      const data = diagnostics[row.slug].metrics;
      let _data = {};
      for (const key in data) {
        const value = data[key];
        const datapoints = value.Datapoints;
        const newest = datapoints[datapoints.length - 1];
        _data[value.Label] = newest.PrettyValue;
      }
      return JsxHelper.createKeyValueFromObject(_data);
    }),
    JsxHelper.createTableActionsHeader(actions, '3%'),
  ];

  // --------------
  // WEBSITES TABLE
  // --------------

  const websitesHeaders = [
    JsxHelper.createTableTextHeader('partner_slug', 'Partner ID', '20%'),
    JsxHelper.createTableWebsiteHeader(allWebsites, '20%'),
    JsxHelper.createTableHiddenHeader('website_slug', 'Website ID')
  ];

  // -----------
  // DIAGNOSTICS
  // -----------

  const renderDiagnostics = () => {
    if (loadingDiagnostics && loadingDiagnostics[currentDatabase.slug]) {
      return JsxHelper.createLoadingSpinner();
    }
    const TABS_INFO = [
      { title: 'InnoDB', value: 'innodb', },
      { title: 'Process List', value: 'processlist', },
      { title: 'Global', value: 'global', },
      { title: 'Metrics', value: 'metrics', },
    ];
    const currentDiagnostics = diagnostics[currentDatabase.slug] || {};
    if (currentDiagnostics && currentDiagnostics.processlist) {
      // Process list
      currentDiagnostics.processlist = currentDiagnostics.processlist.map(item => {
        const website = allWebsites.find(w => w.database_name === item.DB);
        return {
          ID: item.ID,
          WEBSITE: website ? website.slug : '-',
          COMMAND: item.COMMAND,
          INFO: item.INFO,
          TIME_MS: item.TIME_MS,
          STATE: item.STATE,
          DB: item.DB,
          HOST: item.HOST,
          MEMORY_USED: item.MEMORY_USED,
          MAX_MEMORY_USED: item.MAX_MEMORY_USED,
          EXAMINED_ROWS: item.EXAMINED_ROWS,
          SENT_ROWS: item.SENT_ROWS,
          QUERY_ID: item.QUERY_ID,
          TID: item.TID,
          PROGRESS: item.PROGRESS,
        };
      });
    }
    // Render the diagnostics tabs
    return <Fragment>
      <GroupTitle>
        <GroupTitle.Filters>
          {TABS_INFO.map(tab => (
            <button key={tab.value} className={activeDiagnosticsTab === tab.value ? 'active' : ''} onClick={() => setActiveDiagnosticsTab(tab.value)}>{tab.title}</button>
          ))}
        </GroupTitle.Filters>
      </GroupTitle>
      {activeDiagnosticsTab === 'global' ? JsxHelper.createObjectsTable(currentDiagnostics.global) : null}
      {activeDiagnosticsTab === 'processlist' ? JsxHelper.createObjectsTable(currentDiagnostics.processlist) : null}
      {activeDiagnosticsTab === 'innodb' ? JsxHelper.createTextareaInput({
        label: 'InnoDB Status',
        value: currentDiagnostics.innodb,
        style: { width: '75%', height: 'auto' },
        rows: 50,
        class: 'margin-top-24',
        disabled: true,
      }) : null}
      {activeDiagnosticsTab === 'metrics' ? JsxHelper.createTextareaInput({
        label: 'CloudWatch Metrics',
        value: JSON.stringify(currentDiagnostics.metrics, null, 2),
        style: { width: '75%', height: 'auto' },
        rows: 50,
        class: 'margin-top-24',
        disabled: true,
      }) : null}
    </Fragment>;
  }

  return (
    <Fragment>
      <TitleBar>
        {currentScreen === 'databases' && <TitleBar.Title breadcrumbs={JsxHelper.createBreadcrumbs('databases')}>Databases</TitleBar.Title>}
        {currentScreen === 'websites' && <TitleBar.Title breadcrumbs={JsxHelper.createBreadcrumbs('websites', 'databases')}>{currentDatabase.slug}</TitleBar.Title>}
        {currentScreen === 'diagnostics' && <TitleBar.Title breadcrumbs={JsxHelper.createBreadcrumbs('diagnostics', 'databases')}>{currentDatabase.slug}</TitleBar.Title>}
        <TitleBar.Actions>
          {currentScreen === 'diagnostics' && JsxHelper.createButton({
            label: 'Refresh',
            loading: loadingDiagnostics && loadingDiagnostics[currentDatabase.slug],
            disabled: loadingDiagnostics && loadingDiagnostics[currentDatabase.slug],
            onClick: () => getDiagnostics(currentDatabase),
          })}
          {['websites', 'diagnostics'].includes(currentScreen) && JsxHelper.createCloseButton({
            onClick: () => {
              setCurrentScreen('databases');
              setWebsitesData([]);
              setCurrentDatabase(false);
            }
          })}
          {['databases'].includes(currentScreen) && JsxHelper.createButton({
            label: 'Load Diagnostics',
            loading: loadingDiagnostics && Object.keys(loadingDiagnostics).some(key => loadingDiagnostics[key]),
            disabled: loading,
            onClick: () => loadDiagnosticsSync().catch(error => 
              dispatch(setGlobalErrorMsg(error))
            ),
          })}
        </TitleBar.Actions>
      </TitleBar>
      <Content className='main-databases-table'>
        {currentScreen === 'databases' && renderDatabaseAlerts()}
        {currentScreen === 'databases' && <WPSDataTable columns={headers} body={databases} rowsPerPage={25} loading={loading} />}
        {currentScreen === 'websites' && <WPSDataTable columns={websitesHeaders} body={websitesData} rowsPerPage={25} loading={loading}/>}
        {currentScreen === 'diagnostics' && renderDiagnostics()}
        {modal && DialogHelper.inputs({
          title: 'Attach Website',
          onClose: () => setModal(false),
          onConfirm: () => attachWebsite(modal.database, modal.website_slug),
          confirmBtn: 'Attach',
          loading: modal.loading,
          disabled: !modal.website_slug,
          inputs: [{
            name: 'website_slug',
            type: 'select',
            label: 'Website',
            value: modal.website_slug,
            options: WebsiteHelper.buildSelectOptions(allWebsites),
            onChange: e => setModal(prevState => ({ ...prevState, website_slug: e.target.value })),
            required: true,
          }]
        })}
      </Content>
    </Fragment>
  );
};

export default ServerIndex;
